import React, { useState, useMemo, Dispatch, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { payP2P } from '../../api/payP2P'
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation'
import { extraFieldShowFilter } from '../../utils/filters'
import { paymentErrorHandler } from '../../utils/paymentErrorHandler'
import { PaymentMethodType, PaymentPageInfo } from '../../types/paymentPageInfo'
import { getPaymentMethodExtraField } from '../../types/paymentMethod'
import Money from '../../types/Money'
import BankSelect from '../FormSelect/BankSelect'
import RequisiteTypeSelect from '../FormSelect/RequisiteTypeSelect'
import ExtraFields from '../ExtraFields/ExtraFields'
import SubmitButton from '../SubmitButton/SubmitButton'
import styles from './PaymentMethod.module.css'

interface PixPaymentProps {
    paymentPageInfo: PaymentPageInfo
    paymentMethod: PaymentMethodType
    money?: Money
    email: string
    setParentError: (field: string, message: string) => void
    validateParentFields: (fields: Record<string, string>) => boolean
    setPaymentError: Dispatch<SetStateAction<boolean>>
}

const P2PPaymentMethod = ({
    paymentPageInfo,
    paymentMethod,
    money,
    email,
    setParentError,
    validateParentFields,
    setPaymentError,
}: PixPaymentProps) => {
    const navigate = useNavigate()

    const [p2pData, setP2PData] = useState({
        type: '',
        bank: '',
    })

    const [extraFieldsData, setExtraFieldsData] = useState<Record<string, string>>(() => {
        const initialData: Record<string, string> = {}

        paymentMethod.extraFields.forEach(field => {
            initialData[field.name] = field.value || ''
        })

        return initialData
    })

    const [isSubmitting, setIsSubmitting] = useState(false)

    const validationRules = useMemo(() => {
        const rules: ValidationRules = {
            requisiteType: {
                required: false,
                validation: (requisiteType: string): string | null => {
                    if (!requisiteType) {
                        return null
                    }

                    const cleanRequisiteType = requisiteType.trim()

                    if (!/^(card|sbp)$/.test(cleanRequisiteType)) {
                        return t`Тип реквизита должен иметь одно из следующих значений: card, sbp`
                    }

                    return null
                },
            },
            bank: {
                required: false,
                validation: (bank: string): string | null => {
                    return null
                },
            },
        }

        paymentMethod.extraFields
            .filter(extraFieldShowFilter)
            .forEach(field => {
                rules[field.name] = {
                    required: true,
                    validation: getPaymentMethodExtraField(field.name)?.validation,
                }
            })

        return rules
    }, [paymentMethod.extraFields])

    const { errors, setError, removeError, validateAllFields } = useFormValidation(validationRules)

    const handleP2PDataChange = (name: string, value: string) => {
        setP2PData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const handleExtraFieldChange = (name: string, value: string) => {
        setExtraFieldsData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validParentFields = validateParentFields({ email: email, amount: money?.amount || '' })
        const validThisFields = validateAllFields({ ...p2pData, ...extraFieldsData })

        if (!validParentFields || !validThisFields) {
            return
        }

        setIsSubmitting(true)

        try {
            const transaction = await payP2P(paymentPageInfo.id, p2pData, { email: email, ...extraFieldsData }, money)

            navigate(`/payment/${paymentPageInfo.id}/p2p/${transaction.transactionId}`)
        } catch (error) {
            paymentErrorHandler(error, setError, setParentError, setPaymentError)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>{t`Платежные данные`}</h2>

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.cardSection}>
                    <BankSelect
                        label={t`Ваш банк`}
                        value={p2pData.bank}
                        onChange={(value) => handleP2PDataChange('bank', value)}
                        errorMessage={errors.bank || undefined}
                    />

                    <RequisiteTypeSelect
                        label={t`Тип реквизита`}
                        value={p2pData.type}
                        onChange={(value) => handleP2PDataChange('type', value)}
                        errorMessage={errors['type'] || undefined}
                    />
                </div>

                {paymentMethod.extraFields.length > 0 && (
                    <ExtraFields
                        extraFields={paymentMethod.extraFields}
                        data={extraFieldsData}
                        errors={errors}
                        handleExtraFieldChange={handleExtraFieldChange}
                    />
                )}

                <SubmitButton isSubmitting={isSubmitting} />
            </form>
        </div>
    )
}

export default P2PPaymentMethod
